/**
 * TestimonialsSection — Morgan Legal Group
 * Design: Cream background, large testimonial cards with gold left border
 * Auto-rotating carousel with manual controls
 */
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";

const testimonials = [
  {
    name: "Margaret T.",
    location: "Manhattan, NY",
    rating: 5,
    text: "Russel Morgan and his team handled my mother's estate with extraordinary care and professionalism. What could have been an overwhelming process was made clear and manageable at every step. I cannot recommend Morgan Legal Group highly enough to anyone navigating estate matters in New York.",
    service: "Probate & Estate Administration",
  },
  {
    name: "David & Susan K.",
    location: "Brooklyn, NY",
    rating: 5,
    text: "We came to Morgan Legal Group to create our first comprehensive estate plan. The team took the time to truly understand our family's needs and crafted a plan that gave us complete peace of mind. The process was thorough, transparent, and surprisingly straightforward.",
    service: "Estate Planning & Trusts",
  },
  {
    name: "Robert A.",
    location: "Long Island, NY",
    rating: 5,
    text: "After my father passed without a will, I was terrified about the probate process. Morgan Legal Group guided me through every step with patience and expertise. They saved our family significant time, money, and stress during an already difficult period.",
    service: "Probate Administration",
  },
  {
    name: "Elena M.",
    location: "Queens, NY",
    rating: 5,
    text: "The asset protection strategy Morgan Legal Group developed for my business has been invaluable. Russel's knowledge of New York law is exceptional, and his team's responsiveness is unlike anything I've experienced with other law firms. Truly outstanding service.",
    service: "Asset Protection",
  },
  {
    name: "James & Patricia L.",
    location: "Staten Island, NY",
    rating: 5,
    text: "We needed help with Medicaid planning for my aging parents, and Morgan Legal Group provided exactly the guidance we needed. Their elder law expertise helped us protect my parents' assets while ensuring they receive the care they deserve. A true lifeline.",
    service: "Elder Law & Medicaid Planning",
  },
  {
    name: "Thomas W.",
    location: "The Bronx, NY",
    rating: 5,
    text: "From the initial consultation to the final signing, every interaction with Morgan Legal Group was professional, clear, and reassuring. They drafted a comprehensive trust that perfectly reflects our wishes. Worth every penny and more.",
    service: "Revocable Living Trust",
  },
];

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const goTo = (index: number) => {
    setCurrent(index);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
  };

  const prev = () => goTo((current - 1 + testimonials.length) % testimonials.length);
  const next = () => goTo((current + 1) % testimonials.length);

  const t = testimonials[current];

  return (
    <section id="testimonials" className="py-20 lg:py-28 bg-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14">
          <div className="max-w-xl">
            <div className="section-label mb-4">Client Reviews &amp; Video Testimonials</div>
            <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              What Our Clients{" "}
              <span className="text-gold italic">Say About Us</span>
            </h2>
          </div>
          <div className="flex items-center gap-4 mt-6 lg:mt-0">
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}
              </div>
              <p className="font-display text-navy text-2xl font-bold">4.9</p>
              <p className="font-body text-navy/60 text-sm">312 Google Reviews</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="w-10 h-10 border-2 border-navy/20 hover:border-gold hover:bg-gold hover:text-white text-navy flex items-center justify-center transition-all duration-200"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 border-2 border-navy/20 hover:border-gold hover:bg-gold hover:text-white text-navy flex items-center justify-center transition-all duration-200"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main testimonial */}
        <div
          className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="testimonial-card relative overflow-hidden mb-8">
            {/* Large quote mark */}
            <Quote className="absolute top-4 right-6 w-16 h-16 text-gold/10 fill-gold/10" />

            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                  ))}
                </div>
                <blockquote className="font-serif-elegant text-navy text-lg lg:text-xl leading-relaxed italic mb-6">
                  &ldquo;{t.text}&rdquo;
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-navy flex items-center justify-center flex-shrink-0">
                    <span className="font-display text-gold text-lg font-bold">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-body text-navy font-semibold">{t.name}</p>
                    <p className="font-body text-navy/60 text-sm">{t.location}</p>
                    <p className="font-body text-gold text-xs font-medium mt-0.5">{t.service}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center gap-2 justify-center">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-200 ${
                  i === current
                    ? "w-8 h-2 bg-gold"
                    : "w-2 h-2 bg-navy/20 hover:bg-gold/50"
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Mini testimonials grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {testimonials.filter((_, i) => i !== current).slice(0, 3).map((t, i) => (
            <div
              key={t.name}
              className={`bg-white p-5 border border-gold/15 hover:border-gold/40 transition-all duration-200 cursor-pointer hover:shadow-md ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: `${(i + 1) * 100}ms` }}
              onClick={() => goTo(testimonials.indexOf(t))}
            >
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-3 h-3 fill-gold text-gold" />
                ))}
              </div>
              <p className="font-body text-navy/70 text-sm leading-relaxed line-clamp-3 italic mb-3">
                &ldquo;{t.text}&rdquo;
              </p>
              <p className="font-body text-navy text-sm font-semibold">{t.name}</p>
              <p className="font-body text-navy/50 text-xs">{t.location}</p>
            </div>
          ))}
        </div>

        {/* Видимая оговорка при отзывах — требование ревью 30.08. */}
        <p className="font-body text-navy/50 text-xs text-center mt-8">
          Prior results do not guarantee a similar outcome.
        </p>
      </div>
    </section>
  );
}
